import {
  Box,
  createTheme,
  CssBaseline,
  Grid,
  ThemeProvider,
} from "@mui/material";
import { CircularProgress } from "@mui/joy";
import { CurrentUserSelector, getCurrentUser } from "@nerdjs/account-kit";
import { initNetworkAction } from "@nerdjs/nerd-network";
import { clearNotification, NerdNotifier } from "@nerdjs/nerd-ui";
import { THEME_ID as MATERIAL_THEME_ID } from "@mui/material/styles";
import {
  ReactChild,
  ReactChildren,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
} from "react";

import {
  CssVarsProvider as JoyCssVarsProvider,
  useColorScheme as useJoyColorScheme,
} from "@mui/joy/styles";
import { AppRouter } from "../../appRouter";
import { AppConfig } from "../../environement";
import { appBarConfig } from "../config/appBarConfig";
import { getTheme } from "../config/themeConfig";
import { useAppDispatch, useAppSelector } from "../hooks";
import { IntercomButton } from "../intercom/intercomButton";
import { MainDrawer } from "../mainDrawer/mainDrawer";
import { NerdAppBar } from "../nerdAppBar/nerdAppBar";
import styles from "./style.module.scss";

/**
 * App wrapper
 *
 * @returns {ReactElement} App wrapper
 */
export default function NerdApp(): ReactElement {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(CurrentUserSelector);
  const theme = useMemo(() => {
    return getTheme(AppConfig.app.alphaWatermark ?? false);
  }, []);

  useEffect(() => {
    dispatch(initNetworkAction(AppConfig.api));
    dispatch(getCurrentUser());

    document.addEventListener("wheel", function () {
      const activeElement = document.activeElement as HTMLInputElement;
      if (activeElement?.type === "number") {
        activeElement?.blur();
      }
    });
  }, []);

  return (
    <JoyCssVarsProvider theme={theme}>
      <MaterialThemeBridge>
        <CssBaseline enableColorScheme />
        <NotifierWrapper>
          <Box
            sx={{
              backgroundImage: `url("${AppConfig.app.rootBackground}") `,
              display: "flex",
              flex: 1,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
            }}
          >
            <CssBaseline />
            {!currentUser ? (
              <Grid className={styles.circularProgressContainer} container>
                <Grid item>
                  <CircularProgress />
                </Grid>
              </Grid>
            ) : (
              <>
                <NerdAppBar {...appBarConfig} />
                <MainDrawer />
                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    width: 0,
                    display: "flex",
                    minWidth: 0,
                  }}
                >
                  <AppRouter />
                </Box>
                <IntercomButton />
              </>
            )}
          </Box>
        </NotifierWrapper>
      </MaterialThemeBridge>
    </JoyCssVarsProvider>
  );
}

function MaterialThemeBridge({ children }: { children: ReactNode }) {
  const { mode } = useJoyColorScheme();
  const materialTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: mode === "dark" ? "dark" : "light",
          primary: {
            main: "#D3663E",
            light: "#EBA688",
            dark: "#B15130",
            contrastText: "#fff",
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
      {children}
    </ThemeProvider>
  );
}

const NotifierWrapper = ({
  children,
}: {
  children: (ReactChild | ReactChildren) & ReactNode;
}) => {
  const dispatch = useAppDispatch();
  const notification = useAppSelector((s) => s.notification);
  return (
    notification && (
      <NerdNotifier
        {...notification}
        onClose={() => {
          dispatch(clearNotification());
          notification.onClose?.();
        }}
      >
        {children}
      </NerdNotifier>
    )
  );
};
